import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { toast } from 'sonner';

import {
  type StudentMatchResponse,
  useCreateStudentMutation,
  useGetStudentById,
  useUpdateStudentByIdMutation,
} from '~entities/student';
import { Gender } from '~entities/student/modal/student';
import { useCurrentStudio } from '~entities/studios/model/studio-store';

import { useGetCurrency } from '~/shared/api/currency-api';
import { DateTimePicker } from '~/shared/components/date-time-picker';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/shared/shadcn/ui/form';
import { getCountryCodeFromCurrency } from '~/shared/utils/currency-country';
import { phoneInputValueToE164 } from '~/shared/utils/phone-input-e164';
import {
  calculateAgeFromISO,
  extractDateOnly,
  parseToNoonDate,
  toDateOnlyString,
} from '~shared/helpers/datetime';
import { Button } from '~shared/shadcn/ui/button';
import { getErrorMessage } from '~shared/utils/error-handler';

import { useStudentFieldConfig } from '../hooks/use-student-field-config';
import { ConfigurableInputField } from './components/configurable-input-field';
import { ConfigurableSelectField } from './components/configurable-select-field';
import { ConfigurableTextareaField } from './components/configurable-textarea-field';
import { FamilyStudentsSelector } from './components/family-students-selector';
import { StudentMatchDialog } from './components/student-match-dialog';

// Medical field IDs that should be nested under the medical object
// This serves as a single source of truth for which fields belong to the medical object
// Exported for use in schema definitions and other components
export const MEDICAL_FIELD_IDS = [
  'allergies',
  'primaryDoctor',
  'medications',
  'disabilities',
  'specialNeeds',
  'healthInsuranceCarrier',
] as const;

export interface StudentFormData {
  dob: string;
  gender: Gender;
  mobileNo: string;
  firstName: string;
  lastName: string;
  parentId: string;
  studioId: string;
  studentEmail: string;
  tShirtSize: string;
  transportation: string;

  medical: {
    allergies: string;
    primaryDoctor: string;
    medications: string;
    disabilities: string;
    specialNeeds: string;
    healthInsuranceCarrier: string;
  };

  // enrollments: Array<Enrollment>;
  // policies: {
  //   agreedPolicies: Array<{
  //     policyId: string;
  //     title: string;
  //     status: 'agreed' | 'disagreed';
  //   }>;
  // };
}

export const StudentCreationForm = ({
  onSuccess,
  parentId,
}: {
  onSuccess: () => void;
  parentId: string;
}) => {
  const studio = useCurrentStudio();
  const createMutation = useCreateStudentMutation();
  const updateMutation = useUpdateStudentByIdMutation();
  const { getFieldConfig } = useStudentFieldConfig();
  const { data: currencyData } = useGetCurrency();
  const defaultCountry = getCountryCodeFromCurrency(currencyData?.name);
  const [showMatchDialog, setShowMatchDialog] = useState(false);
  const [matchResponse, setMatchResponse] =
    useState<StudentMatchResponse | null>(null);
  const [selectedExistingStudentId, setSelectedExistingStudentId] = useState<
    string | undefined
  >();

  const form = useForm<StudentFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      dob: '',
      gender: undefined,
      mobileNo: '',
      studentEmail: '',
      tShirtSize: '',
      transportation: '',
      parentId: '',
      medical: {
        allergies: '',
        primaryDoctor: '',
        medications: '',
        disabilities: '',
        specialNeeds: '',
        healthInsuranceCarrier: '',
      },
    },
  });

  const { control, handleSubmit } = form;

  const onSubmit = async (data: StudentFormData) => {
    if (parentId && studio) {
      try {
        // If editing existing student, use update mutation
        if (selectedExistingStudentId) {
          await updateMutation.mutateAsync({
            studentId: selectedExistingStudentId,
            firstName: data.firstName,
            lastName: data.lastName,
            dob: data.dob,
            gender: data.gender,
            mobileNo: data.mobileNo,
            studentEmail: data.studentEmail,
            tShirtSize: data.tShirtSize,
            transportation: data.transportation,
            familyName: data.lastName, // Use lastName as familyName
            medical: data.medical,
          });
          toast.success('Student updated successfully');
          onSuccess();
          return;
        }

        // Creating new student
        const result = await createMutation.mutateAsync({
          ...data,
          parentId: parentId,
          studioId: studio._id,
        });

        // Check if result is a match response
        if (result && typeof result === 'object' && 'matchType' in result) {
          const matchResult = result as StudentMatchResponse;

          if (matchResult.matchType === 'near') {
            // Show dialog for near match
            setMatchResponse(matchResult);
            setShowMatchDialog(true);
            return;
          }
        }

        // Success - student created
        toast.success('Student created successfully');
        onSuccess();
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(error);
        toast.error(errorMessage);
      }
    } else {
      onSuccess();
    }
  };

  const handleUseExistingStudent = async () => {
    if (matchResponse?.existingStudent) {
      // Navigate to existing student or close form
      toast.success('Using existing student');
      onSuccess();
    }
  };

  const handleCreateNewStudent = async () => {
    if (parentId && studio) {
      try {
        // Create with skipMatching flag
        await createMutation.mutateAsync({
          ...form.getValues(),
          parentId: parentId,
          studioId: studio._id,
          skipMatching: true,
        });
        toast.success('Student created successfully');
        onSuccess();
      } catch (error) {
        const errorMessage = getErrorMessage(error);
        toast.error(errorMessage);
      }
    }
  };

  const handleSelectExistingStudent = (studentId: string) => {
    setSelectedExistingStudentId(studentId);
  };

  // Fetch student data when selected
  const { data: selectedStudent } = useGetStudentById(
    selectedExistingStudentId || '',
  );

  // Populate form when student data is loaded
  useEffect(() => {
    if (selectedStudent && selectedExistingStudentId) {
      form.reset({
        firstName: selectedStudent.firstName || '',
        lastName: selectedStudent.lastName || '',
        dob: extractDateOnly(selectedStudent.dob) ?? '',
        gender: selectedStudent.gender || undefined,
        mobileNo: selectedStudent.mobileNo || '',
        studentEmail: selectedStudent.studentEmail || '',
        tShirtSize: selectedStudent.tShirtSize || '',
        transportation: selectedStudent.transportation || '',
        parentId: parentId || '',
        studioId: studio?._id || '',
        medical: {
          allergies: selectedStudent.medical?.allergies || '',
          primaryDoctor: selectedStudent.medical?.primaryDoctor || '',
          medications: selectedStudent.medical?.medications || '',
          disabilities: selectedStudent.medical?.disabilities || '',
          specialNeeds: selectedStudent.medical?.specialNeeds || '',
          healthInsuranceCarrier:
            selectedStudent.medical?.healthInsuranceCarrier || '',
        },
      });
      toast.success('Student information loaded. You can update and save.');
    }
  }, [selectedStudent, selectedExistingStudentId, form, parentId, studio]);

  const handleAddNew = () => {
    setSelectedExistingStudentId(undefined);
    form.reset();
  };

  const dateOfBirth = form.watch('dob');
  const age = calculateAgeFromISO(dateOfBirth);

  const firstNameConfig = getFieldConfig('firstName');
  const lastNameConfig = getFieldConfig('lastName');
  const genderConfig = getFieldConfig('gender');
  const mobileNoConfig = getFieldConfig('primaryPhone');
  const studentEmailConfig = getFieldConfig('email');
  const tShirtSizeConfig = getFieldConfig('tShirtSize');
  const transportationConfig = getFieldConfig('transportation');
  const healthInsuranceConfig = getFieldConfig('healthInsuranceCarrier');
  const primaryDoctorConfig = getFieldConfig('primaryDoctor');
  const allergiesConfig = getFieldConfig('allergies');
  const disabilitiesConfig = getFieldConfig('disabilities');
  const medicationsConfig = getFieldConfig('medications');
  const specialNeedsConfig = getFieldConfig('specialNeeds');

  // First name, last name, and DOB are locked required fields in Registration
  // Form Config. Validate them independently of potentially stale saved flags.
  const firstNameRules = {
    required: 'First name is required',
    minLength: {
      value: 2,
      message: 'First name must be at least 2 characters',
    },
    pattern: {
      value: /^[a-zA-Z\s'-]+$/,
      message:
        'First name can only contain letters, spaces, hyphens and apostrophes',
    },
  };
  const lastNameRules = {
    required: 'Last name is required',
    minLength: {
      value: 2,
      message: 'Last name must be at least 2 characters',
    },
    pattern: {
      value: /^[a-zA-Z\s'-]+$/,
      message:
        'Last name can only contain letters, spaces, hyphens and apostrophes',
    },
  };

  return (
    <>
      {parentId && studio && (
        <FamilyStudentsSelector
          parentId={parentId}
          studioId={studio._id}
          selectedStudentId={selectedExistingStudentId}
          onSelectStudent={handleSelectExistingStudent}
          onAddNew={handleAddNew}
        />
      )}
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className=''>
          <div className='mb-4 grid grid-cols-1 gap-4 md:grid-cols-2'>
            <ConfigurableInputField
              control={control}
              name='firstName'
              config={{ ...firstNameConfig, visible: true }}
              placeholder='First Name'
              customRules={firstNameRules}
            />
            <ConfigurableInputField
              control={control}
              name='lastName'
              config={{ ...lastNameConfig, visible: true }}
              placeholder='Last Name'
              customRules={lastNameRules}
            />
            <Controller
              name='dob'
              control={control}
              rules={{
                required: 'Birthdate is required',
                validate: (value) =>
                  !value ||
                  new Date(value) <= new Date() ||
                  'Birthdate cannot be in the future',
              }}
              render={({ field, fieldState: { error } }) => (
                <div className='flex flex-col gap-1'>
                  <DateTimePicker
                    hourCycle={12}
                    granularity='day'
                    displayFormat={{
                      hour12: 'PPP',
                    }}
                    disabledDays={(date) => date > new Date()}
                    placeholder='Birthdate *'
                    value={parseToNoonDate(field.value) ?? undefined}
                    onChange={(date) => {
                      field.onChange(toDateOnlyString(date));
                    }}
                  />
                  {error && (
                    <p className='text-[0.8rem] font-medium text-destructive'>
                      {error.message}
                    </p>
                  )}
                </div>
              )}
            />
            <ConfigurableSelectField
              control={control}
              name='gender'
              config={genderConfig}
              placeholder='Gender'
              options={Object.values(Gender).map((gender) => ({
                value: gender,
                label: gender,
              }))}
            />
            <input
              className='w-full rounded-lg border px-3 py-2 text-gray-700 focus:border-blue-500 focus:outline-none'
              placeholder='Age'
              value={`Age: ${age.years} year${age.years > 1 ? 's' : ''} and ${age.months} month${age.months > 1 ? 's' : ''}`}
              disabled
            />
            {mobileNoConfig?.visible && (
              <FormField
                control={control}
                name='mobileNo'
                rules={{
                  required: mobileNoConfig.required
                    ? 'Student Number is required'
                    : false,
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Student Number{mobileNoConfig.required ? ' *' : ''}
                    </FormLabel>
                    <FormControl>
                      <PhoneInput
                        country={defaultCountry}
                        disableCountryGuess={true}
                        value={field.value || ''}
                        onChange={(value) => {
                      field.onChange(phoneInputValueToE164(value));
                    }}
                        inputStyle={{ width: '100%' }}
                        enableSearch={true}
                        searchPlaceholder='Search country...'
                        searchNotFound='No country found'
                        enableAreaCodes={true}
                        countryCodeEditable={true}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <ConfigurableInputField
              control={control}
              name='studentEmail'
              config={studentEmailConfig}
              placeholder='Student Email'
              type='email'
            />
            <ConfigurableSelectField
              control={control}
              name='tShirtSize'
              config={tShirtSizeConfig}
              placeholder='Costume Size'
              options={tShirtSizeConfig?.options ?? []}
            />
            <ConfigurableInputField
              control={control}
              name='transportation'
              config={transportationConfig}
              placeholder='Transportation'
            />
            <ConfigurableInputField
              control={control}
              name='medical.healthInsuranceCarrier'
              config={healthInsuranceConfig}
              placeholder='Health Insurance Carrier'
            />
            <ConfigurableInputField
              control={control}
              name='medical.primaryDoctor'
              config={primaryDoctorConfig}
              placeholder='Primary Doctor'
            />
          </div>

          <ConfigurableTextareaField
            control={control}
            name='medical.allergies'
            config={allergiesConfig}
            placeholder='Allergies'
            rows={2}
          />
          <ConfigurableTextareaField
            control={control}
            name='medical.disabilities'
            config={disabilitiesConfig}
            placeholder='Disabilities'
            rows={2}
          />
          <ConfigurableTextareaField
            control={control}
            name='medical.medications'
            config={medicationsConfig}
            placeholder='Medications'
            rows={2}
          />
          <ConfigurableTextareaField
            control={control}
            name='medical.specialNeeds'
            config={specialNeedsConfig}
            placeholder='Special Needs'
            rows={2}
          />

          <Button
            type='submit'
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {(createMutation.isPending || updateMutation.isPending) && (
              <Loader2 className='mr-2 size-4 animate-spin' />
            )}
            {selectedExistingStudentId ? 'Update Student' : 'Submit'}
          </Button>
        </form>
      </Form>
      {matchResponse && matchResponse.matchType === 'near' && (
        <StudentMatchDialog
          open={showMatchDialog}
          onOpenChange={setShowMatchDialog}
          existingStudent={matchResponse.existingStudent!}
          newStudentName={`${form.getValues().firstName} ${form.getValues().lastName}`}
          onUseExisting={handleUseExistingStudent}
          onCreateNew={handleCreateNewStudent}
          isPending={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </>
  );
};
